import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api, unwrap } from '@/api/client';
import { plansQuery } from '@/api/queries';
import type { Organization } from '@/api/types';
import { paths } from '@/app/paths';
import { Button, Chip, FieldHint, FileTile, IconInput, Input, Modal, ModalFooter, ModalHeader, Segmented, useModalTitleId, useToast } from '@/design-system';
import { errorField, errorText } from '@/shell/errorText';
import { createOrganization, uploadMedia } from './api';
import s from './organizations.module.css';
import { TelegramSourceField } from './TelegramSourceField';

const TRIAL = 'trial';
// The source notice's version (owner decision Q4; the API's SOURCE_NOTICE_VERSION). Bump it with the text.
export const SOURCE_NOTICE_VERSION = '2026-11-02' as const;

/**
 * «Новая организация» (DESIGN_SPEC §4.12, AC-01): brand, legal name, industry, the three public sources,
 * the logo and catalog files, the plan. POST /admin/organizations (research starts by itself when there
 * are sources, TZ §6 — the sub-line says so, §9 C1), then the files go to /media; a catalog alone starts
 * the research once it is uploaded. Once a link is typed, the source notice appears under the inputs: Studio
 * takes the public information and photos from them (owner decision Q4). Creating with it shown sends its
 * version, which is the client's consent; the empty modal stays as the design draws it. A name typed in the
 * Telegram input (not a link) lists the public channels Telegram finds (`TelegramSourceField`, additive).
 */
export function NewOrganizationModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const plans = useQuery(plansQuery).data ?? [];
  const industries = t('new.industries', { returnObjects: true }) as string[];
  const [brand, setBrand] = useState('');
  const [legal, setLegal] = useState('');
  const [industry, setIndustry] = useState(industries[0] ?? '');
  const [website, setWebsite] = useState('');
  const [instagram, setInstagram] = useState('');
  const [telegram, setTelegram] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [catalog, setCatalog] = useState<File | null>(null);
  // The design's default is «Бизнес»: the highlighted plan.
  const [plan, setPlan] = useState<string | null>(null);
  const planValue = plan ?? plans.find((p) => p.highlighted)?.id ?? plans[0]?.id ?? TRIAL;
  const [problem, setProblem] = useState<{ field: string | null; text: string } | null>(null);
  const hasLink = Boolean(website.trim() || instagram.trim() || telegram.trim());

  const create = useMutation({
    mutationFn: async (): Promise<{ org: Organization; started: boolean; failed: string[] }> => {
      const sources = {
        website: website.trim() || null,
        instagram: instagram.trim() || null,
        telegram: telegram.trim() || null,
        ...(hasLink ? { notice_version: SOURCE_NOTICE_VERSION } : {}),
      };
      const org = await createOrganization({
        brand_name: brand.trim(),
        legal_name: legal.trim() || null,
        industry: industry || null,
        sources,
        ...(planValue === TRIAL ? { status: 'trial' as const } : { plan_id: planValue }),
      });
      const failed: string[] = [];
      for (const [file, purpose] of [
        [logo, 'logo'],
        [catalog, 'catalog'],
      ] as const) {
        if (!file) continue;
        try {
          await uploadMedia(org.id, file, purpose);
        } catch (err) {
          failed.push(t('new.upload_failed', { name: file.name, reason: errorText(err, tc) }));
        }
      }
      const hadSources = Object.values(sources).some(Boolean);
      // A catalog is a source too, but it arrives after the organization: start the research for it.
      let started = hadSources;
      if (!hadSources && catalog && !failed.length) {
        await unwrap(api.POST('/admin/organizations/{org_id}/research-runs', { params: { path: { org_id: org.id } } }));
        started = true;
      }
      return { org, started, failed };
    },
    onSuccess: ({ org, started, failed }) => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast(failed[0] ?? (started ? t('new.created') : t('new.created_no_sources')));
      onClose();
      navigate(paths.organization(org.id, 'overview'));
    },
    onError: (err) => setProblem({ field: errorField(err), text: errorText(err, tc) }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!brand.trim()) {
      setProblem({ field: 'brand_name', text: t('new.brand_required') });
      return;
    }
    setProblem(null);
    create.mutate();
  };

  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('new.title')} sub={t('new.sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <form className={s.form} onSubmit={submit} noValidate>
        <div className={s.grid2}>
          <Input
            label={t('new.brand')}
            placeholder={t('new.brand_placeholder')}
            value={brand}
            aria-invalid={problem?.field === 'brand_name' || undefined}
            onChange={(e) => setBrand(e.target.value)}
          />
          <Input label={t('new.legal')} placeholder={t('new.legal_placeholder')} value={legal} onChange={(e) => setLegal(e.target.value)} />
        </div>
        <div className={s.group}>
          <span className={s.groupLabel} id={`${titleId}-ind`}>
            {t('new.industry')}
          </span>
          <div className={s.chips} role="group" aria-labelledby={`${titleId}-ind`}>
            {industries.map((x) => (
              <Chip key={x} variant="light" selected={industry === x} onClick={() => setIndustry(x)}>
                {x}
              </Chip>
            ))}
          </div>
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('new.sources')}</span>
          <IconInput icon="globe" placeholder={t('new.website')} aria-label={t('new.website')} value={website} onChange={(e) => setWebsite(e.target.value)} />
          <div className={s.grid2}>
            <IconInput icon="instagram" placeholder={t('new.instagram')} aria-label={t('new.instagram')} value={instagram} onChange={(e) => setInstagram(e.target.value)} />
            <TelegramSourceField value={telegram} onChange={setTelegram} />
          </div>
          {hasLink && <FieldHint>{t('new.source_notice')}</FieldHint>}
        </div>
        <div className={s.grid2}>
          <FileTile icon="image-up" accept="image/svg+xml,image/png,image/jpeg,image/webp,.jpg,.jpeg" onFiles={(f) => setLogo(f[0] ?? null)}>
            {logo ? <span className={s.fileName}>{logo.name}</span> : t('new.logo')}
          </FileTile>
          <FileTile icon="file-spreadsheet" accept=".xlsx,.xls,.csv,.pdf,text/csv" onFiles={(f) => setCatalog(f[0] ?? null)}>
            {catalog ? <span className={s.fileName}>{catalog.name}</span> : t('new.catalog')}
          </FileTile>
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('new.plan')}</span>
          <Segmented
            variant="form"
            label={t('new.plan')}
            value={planValue}
            onChange={setPlan}
            className={s.alignStart}
            options={[{ value: TRIAL, label: t('new.trial') }, ...plans.map((p) => ({ value: p.id, label: p.name }))]}
          />
        </div>
        {problem && (
          <span className={s.problem} role="alert">
            {problem.text}
          </span>
        )}
        <ModalFooter padTop>
          <Button size={46} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={46} type="submit" trailingIcon="arrow-right" disabled={create.isPending}>
            {t('new.submit')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
